// ComponentsDetailed.tsx
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AccountEntry, CategoryOption, Project } from '../../types';
import { 
  calculateCategoryTotal,
  calculateTTM,
  calculateTTMForPeriodType,
  calculateAccountsByPeriod,
  getQuarter,
  getYear 
} from '../../lib/financial/calculations';

interface HeaderProps {
  title: string;
  isOpen: boolean;
  onClick: () => void;
  colSpan?: number;
}

export const SectionHeader: React.FC<HeaderProps> = ({ 
  title, 
  isOpen, 
  onClick, 
  colSpan
}) => (
  <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={onClick}>
    <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-black border-r border-gray-200">
      <div className="flex items-center">
        {isOpen ? (
          <ChevronDown className="h-4 w-4 mr-2" />
        ) : (
          <ChevronRight className="h-4 w-4 mr-2" />
        )}
        <span>{title}</span>
      </div>
    </td>
    <td className="px-6 py-2 text-sm text-gray-600" />
    <td colSpan={colSpan} />
  </tr>
);

interface CategoryRowProps {
  category: string;
  periods: string[];
  accounts: AccountEntry[];
  viewType: 'monthly' | 'quarterly' | 'yearly';
  allMonths: string[];
  isRevenue: boolean;
  isContraRevenue: boolean;
  templateCategories?: CategoryOption[];
  isExpanded: boolean;
  onToggle: () => void;
  selectedProject?: Project | null;
}

export const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  periods,
  accounts,
  viewType,
  allMonths,
  isRevenue,
  isContraRevenue,
  templateCategories,
  isExpanded,
  onToggle,
  selectedProject
}) => {
  // Get the display name from template categories if available
  const getCategoryDisplayName = (categoryValue: string) => {
    if (!templateCategories) return categoryValue;
    
    const templateCategory = templateCategories.find(cat => cat.value === categoryValue);
    return templateCategory?.label || categoryValue;
  };

  const categoryAccounts = React.useMemo(() => {
    const accountMap = new Map<string, AccountEntry[]>();
    
    accounts
      .filter(acc => acc.category === category)
      .forEach(acc => {
        if (!accountMap.has(acc.accountNumber)) {
          accountMap.set(acc.accountNumber, []);
        }
        accountMap.get(acc.accountNumber)?.push(acc);
      });

      return Array.from(accountMap.entries())
      .map(([accountNumber, entries]) => ({
        accountNumber,
        name: entries[0].name,
        entries
      }))
      .sort((a, b) => parseInt(a.accountNumber) - parseInt(b.accountNumber));
  }, [accounts, category]);

  const hasNonZeroValues = React.useMemo(() => {
    return periods.some(period => {
      const total = calculateCategoryTotal(
        accounts,
        category,
        period,
        viewType,
        isRevenue,
        isContraRevenue
      );
      return Math.abs(total) > 0;
    });
  }, [accounts, category, periods, viewType, isRevenue, isContraRevenue]);

  if (!hasNonZeroValues) {
    return null;
  }

  return (
    <>
      <tr className="cursor-pointer hover:bg-gray-50" onClick={onToggle}>
        <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm text-black border-r border-gray-200">
          <div className="flex items-center">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 mr-2" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2" />
            )}
            {getCategoryDisplayName(category)}
          </div>
        </td>
        <td className="px-6 py-2 text-sm text-gray-600" />{/* Account Number column */}
        {periods.map(period => (
          <td key={period} className="px-6 py-2 text-sm text-gray-900 text-right">
            {calculateCategoryTotal(
              accounts,
              category,
              period,
              viewType,
              isRevenue,
              isContraRevenue
            ).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}
          </td>
        ))}
        {viewType === 'yearly' && (
          <td className="px-6 py-2 text-sm text-gray-900 text-right">
            {(() => {
              const ttmAmount = calculateTTMForPeriodType(
                accounts,
                category,
                allMonths,
                isRevenue,
                isContraRevenue,
                selectedProject?.period_type || 'monthly'
              );
              return ttmAmount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              });
            })()}
          </td>
        )}
      </tr>
      {isExpanded && categoryAccounts.map(({accountNumber, name, entries}) => (
        <tr key={accountNumber} className="bg-gray-50">
          <td className="sticky left-0 z-10 bg-gray-50 pl-12 py-2 text-sm text-gray-600 border-r border-gray-200">
            {name}
          </td>
          <td className="px-6 py-2 text-sm text-gray-600">
            {accountNumber}
          </td>
          {periods.map(period => {
            let amount = 0;
            if (viewType === 'monthly') {
              const periodEntry = entries.find(entry => entry.month === period);
              amount = periodEntry ? 
                (isRevenue ? 
                  (periodEntry.credit - periodEntry.debit)
                  : isContraRevenue ? 
                    -(periodEntry.credit - periodEntry.debit)
                    : (periodEntry.debit - periodEntry.credit))
                : 0;
            } else if (viewType === 'quarterly') {
              amount = entries
                .filter(entry => getQuarter(entry.month) === period)
                .reduce((sum, entry) => 
                  sum + (isRevenue ? 
                    (entry.credit - entry.debit)
                    : isContraRevenue ? 
                      -(entry.credit - entry.debit)
                      : (entry.debit - entry.credit))
                , 0);
            } else {
              amount = entries
                .filter(entry => getYear(entry.month) === period)
                .reduce((sum, entry) => 
                  sum + (isRevenue ? 
                    (entry.credit - entry.debit)
                    : isContraRevenue ? 
                      -(entry.credit - entry.debit)
                      : (entry.debit - entry.credit))
                , 0);
            }

            return (
              <td key={period} className="px-6 py-2 text-sm text-gray-600 text-right">
                {amount.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </td>
            );
          })}
          {viewType === 'yearly' && (
            <td className="px-6 py-2 text-sm text-gray-600 text-right">
              {(() => {
                const ttmAmount = entries
                  .filter(entry => allMonths.slice(-12).includes(entry.month))
                  .reduce((sum, entry) => 
                    sum + (isRevenue ? 
                      (entry.credit - entry.debit)
                      : isContraRevenue ? 
                        -(entry.credit - entry.debit)
                        : (entry.debit - entry.credit))
                  , 0);
                return ttmAmount.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                });
              })()}
            </td>
          )}
        </tr>
      ))}
    </>
  );
};

interface SectionTotalProps {
  title: string;
  periods: string[];
  total: (period: string) => number;
  ttmTotal?: number;
  viewType?: 'monthly' | 'quarterly' | 'yearly';
  className?: string;
}

export const SectionTotal: React.FC<SectionTotalProps> = ({
  title,
  periods,
  total,
  ttmTotal,
  viewType,
  className = "bg-gray-200"
}) => {
  return (
    <tr className={className}>
      <td className="sticky left-0 z-10 bg-inherit px-6 py-3 text-sm font-semibold text-black border-r border-gray-200">
        {title}
      </td>
      <td className="px-6 py-2 text-sm text-gray-600" />
      {periods.map(period => (
        <td key={period} className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
          {total(period).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </td>
      ))}
      {ttmTotal !== undefined && (
        <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
          {ttmTotal.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </td>
      )}
    </tr>
  );
};
